// Onsite "Mobile Number": reads the participant's own number from person_data and
// builds the patch that saves an edit to Registration's Mobile field (container1).
import {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';

export const PHONE_ERROR = 'Please enter a valid phone number for the selected country.';
export const MOBILE_REQUIRED_ERROR = 'Please enter a mobile number.';
export const DIAL_REQUIRED_ERROR = 'Please select a country code.';

const DEFAULT_DIAL = '+1';

// First non-empty wins: current Registration, then Accreditation, older forms, telephone keys.
// Caddie (phoneGroup2, mobileTelephoneNumber5, top-level phoneNumber1 and mobileTelephoneNumber) and
// guest (dataGrid, phoneNumber.phoneNumber1) keys are left out because they hold other people's numbers.
const SOURCES = [
  ['container1.textField', 'container1.select'],
  ['container.mobileTelephoneNumber', 'container.countryCode'],
  ['mobileTelephoneNumber1.mobileTelephoneNumber', 'mobileTelephoneNumber1.country1'],
  ['fullTelephoneNumber.mobileTelephoneNumber', 'fullTelephoneNumber.country'],
  ['telephoneContainer.textField', 'telephoneContainer.countryCode'],
  ['phoneNumber.phoneNumber', 'phoneNumber.country'],
  ['telephoneNumber.phoneNumber', 'telephoneNumber.country'],
  ['telephoneNumber', null],
];

const getPath = (obj, path) => path.split('.').reduce((value, key) => value?.[key], obj);

// Country selects save an ISO code ("AX"); older forms may save { dial_code: "+358" }.
const dialFor = (country) => {
  if (country && typeof country === 'object') return String(country.dial_code || '').trim();
  const code = String(country || '').trim().toUpperCase();
  if (!code || code.startsWith('+')) return code;
  try {
    return `+${getCountryCallingCode(code)}`;
  } catch {
    return '';
  }
};

// ISO code for the Registration dropdown, from a saved code or a { code } object.
const selectFor = (country) => {
  const code = String(country?.code ?? country ?? '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : '';
};

export const readOnsiteMobile = (personData) => {
  for (const [numberPath, countryPath] of SOURCES) {
    const raw = getPath(personData, numberPath);
    if (typeof raw !== 'string' && typeof raw !== 'number') continue;
    const number = String(raw).trim();
    if (!number) continue;

    if (number.startsWith('+')) {
      const parsed = parsePhoneNumberFromString(number);
      if (parsed) {
        return {
          number: parsed.nationalNumber,
          dial: `+${parsed.countryCallingCode}`,
          select: parsed.country || '',
        };
      }
    }
    const country = countryPath ? getPath(personData, countryPath) : null;
    return {
      number,
      // Blank when unknown: guessing +1 would show, and could save, the wrong country.
      dial: dialFor(country),
      select: selectFor(country),
    };
  }
  return { number: '', dial: DEFAULT_DIAL, select: '' };
};

// Country for the Registration dropdown: the number's own country, else the saved one if
// the dial code is unchanged, else the dial code's main country (listed first by the library).
const countryFor = (original, dial, number) =>
  parsePhoneNumberFromString(dial + number)?.country
  || (dial === original.dial && original.select)
  || metadata.country_calling_codes[dial.slice(1)]?.[0]
  || '';

const isUnchanged = (original, dial, number) =>
  number === original.number && dial === original.dial;

// Only an edited number is checked, so an old badly formatted one can't block SUBMIT.
export const getMobileError = (original, dial, rawNumber) => {
  const number = rawNumber.trim();
  if (isUnchanged(original, dial, number)) return '';
  if (!number) return original.number ? MOBILE_REQUIRED_ERROR : '';
  if (!dial) return DIAL_REQUIRED_ERROR;
  return isPossiblePhoneNumber(dial + number) ? '' : PHONE_ERROR;
};

// Returns person_data keys to send. Empty when nothing changed, so an untouched
// number shown from an older key is never copied into container1.
export const buildMobilePatch = (original, dial, rawNumber) => {
  const number = rawNumber.trim();
  if (!number || isUnchanged(original, dial, number)) return {};
  const digits = number.replace(/\D/g, '');
  return { container1: { textField: digits, select: countryFor(original, dial, digits) } };
};
